import { Map } from "immutable";
import { z } from "zod/v4";

import { Tuple } from "../Tuple";
import {
	type Arrangement,
	ZSet,
	type ZTuple,
	dehydrateArrangement,
	dehydrateZTuple,
	hydrateArrangement,
	hydrateZTuple,
	rawArrangementSchema,
	rawZTupleSchema,
} from "../ZSet";
import { pluck } from "../jsonTable/pluck";
import { type BilinearOperatorLogic } from "./BilinearOperatorLogic";
import {
	type JsonPathRef,
	type SubqueryRef,
	jsonpathRefSchema,
	subqueryRefSchema,
} from "./schema-util";

export function leftOuterJoin<L, R, Out>(
	spec: LeftOuterJoinSpec,
	makeOutput: (l: Tuple<L>, r: Tuple<R> | Tuple<R[]> | null) => Tuple<Out>
): BilinearOperatorLogic<L, R, Out, LeftOuterJoinState<L, R, Out>> {
	const kL = (t: Tuple<L>) => t.pluck(spec.leftKey.path);
	const kR = (t: Tuple<R>) => t.pluck(spec.rightKey.path);

	return (
		leftΔ: ZSet<L>,
		rightΔ: ZSet<R>,
		state: LeftOuterJoinState<L, R, Out> = {
			type: "leftOuterJoin",
			leftByK: Map(),
			rightByK: Map(),
			aggregateByK: Map(),
		}
	): [ZSet<Out>, LeftOuterJoinState<L, R, Out>] => {
		//
		// Implementation notes: See the implementation notes for `join` for a detailed explanation
		// of joins generally. `join` implements an `INNER JOIN`, while this function implements a
		// `LEFT OUTER JOIN`. There are two main differences:
		//
		// 1. All records from the left table are included, even if there's no match in the right
		//    table.
		// 2. When there's no match, we use a "NULL" value (represented as the JavaScript `null` in
		//    our implementation). "NULL" values have a weight of 1.
		//
		// In other words, the decomposition is similar to `join` (which is an `INNER JOIN`), but we
		// need to handle the case where there's no match in the right table, so we add a final term
		// `(ΔL ⋈ NULL)`, which represents new left rows that don't match any right rows.
		//
		//     Δ(L ⋈ R) = (ΔL ⋈ R) + (L ⋈ ΔR) + (ΔL ⋈ ΔR) + (ΔL ⋈ NULL)
		//
		// Time complexity remains the same as `join`:
		//
		//     time = O( |ΔL|·|S_k| + |ΔS|·|R_k| + |ΔL∩ΔS|·|ΔL_k|·|ΔS_k| )
		//     mem  = O( distinctKeys(R) + distinctKeys(S) + Δ batch size )
		//

		let out = ZSet.empty<Out>();

		const newState = {
			type: "leftOuterJoin",
			leftByK: state.leftByK,
			rightByK: state.rightByK,
			aggregateByK: state.aggregateByK,
		} satisfies LeftOuterJoinState<L, R, Out>;

		/* --- 1. ΔL ⋈ R --- */
		for (const l of leftΔ) {
			const keyId = kL(l.tuple).contentId();
			const rightBag = state.rightByK.get(keyId, ZSet.empty<R>());

			if (spec.output.type === "insertAggregateAtPath") {
				// Collect all matching right records into an array
				const rightRecords: R[] = [];
				for (const r of rightBag) {
					rightRecords.push(r.tuple.getValue());
				}

				// Create output with the array
				const prevAggregate = state.aggregateByK.get(keyId);
				if (prevAggregate) {
					// Left-side diffs will arrive with both the -1 and the +1 present
					// in the delta list if appropriate and do not need explicit -1 handling...
					state.aggregateByK = state.aggregateByK.remove(keyId);
				}

				const zOutput = {
					tuple: makeOutput(
						l.tuple,
						Tuple.from<R[]>(
							`aggregate(${l.tuple.id})`,
							l.tuple.getOwner(),
							rightRecords
						)
					),
					weight: l.weight * 1,
				};
				out = out.add(zOutput);
				newState.aggregateByK = newState.aggregateByK.set(keyId, zOutput);

				continue;
			}

			if (rightBag.size === 0) {
				// No match in right table, output with NULL (weight 1)
				out = out.add({
					tuple: makeOutput(l.tuple, null),
					weight: l.weight * 1,
				});
			} else {
				// Has matches in right table
				for (const r of rightBag) {
					out = out.add({
						tuple: makeOutput(l.tuple, r.tuple),
						weight: l.weight * r.weight,
					});
				}
			}
		}

		/* --- 2. L ⋈ ΔR --- */
		// First, check if any existing left records that were NULL-joined now have matches
		for (const r of rightΔ) {
			const key = kR(r.tuple);
			const keyId = key.contentId();
			const leftBag = state.leftByK.get(keyId, ZSet.empty<L>());

			// For each left record that matches this new right record
			for (const l of leftBag) {
				if (spec.output.type === "insertAggregateAtPath") {
					const prevAggregate = newState.aggregateByK.get(keyId);
					if (prevAggregate) {
						// ...but right-side diffs in aggregate scenarios need to be multiplied.
						//  * if sending dR +1 or -1 then we need to have both -1/+1 LxdR because it
						//    affects the count of the aggregate, not the row itself
						//  * if sending dR -1/+1 together (e.g. an edit) then we should get 4 tuples
						//    (-1/+1/-1/+1) to the count (which will be compacted out).
						out = out.add({ tuple: prevAggregate.tuple, weight: -1 });
						newState.aggregateByK = newState.aggregateByK.remove(keyId);
					}

					// Collect all matching right records into an array
					const rightRecords = state.rightByK
						.get(keyId, ZSet.empty<R>())
						.add(r)
						.toArray()
						.map((z) => z.tuple.getValue());

					const zOutput = {
						tuple: makeOutput(
							l.tuple,
							Tuple.from<R[]>(
								`aggregate(${l.tuple.id})`,
								l.tuple.getOwner(),
								rightRecords
							)
						),
						weight: l.weight * 1,
					};
					out = out.add(zOutput);
					newState.aggregateByK = newState.aggregateByK.set(keyId, zOutput);

					continue;
				}

				// we need to remove the NULL-joined tuple if it exists for l
				const nullTuple = makeOutput(l.tuple, null);
				const existingWeight = out?.weight(nullTuple) ?? 0;
				if (existingWeight >= 0) {
					out = out.add({ tuple: nullTuple, weight: -1 });
				}
				// Then add the new match
				out = out.add({ tuple: makeOutput(l.tuple, r.tuple), weight: l.weight * r.weight });
			}
		}

		/* --- 3. ΔL ⋈ ΔR --- */
		const rightBuckets = Map<string, ZSet<R>>().withMutations((b) => {
			for (const rec of rightΔ) {
				const keyId = kR(rec.tuple).contentId();
				const bag = b.get(keyId, ZSet.empty<R>());
				b.set(keyId, bag.add(rec));
			}
		});

		for (const l of leftΔ) {
			const keyId = kL(l.tuple).contentId();
			const bucketR = rightBuckets.get(keyId);
			if (!bucketR) {
				continue;
			}

			for (const r of bucketR) {
				if (spec.output.type === "insertAggregateAtPath") {
					const prevAggregate = newState.aggregateByK.get(keyId);
					if (prevAggregate) {
						out = out.add({ tuple: prevAggregate.tuple, weight: -1 });
						newState.aggregateByK = newState.aggregateByK.remove(keyId);
					}

					// Collect all matching right records into an array
					const prevRecords = pluck(
						prevAggregate?.tuple.getValue(),
						spec.output.path.path
					);
					const rightRecords: R[] = Array.isArray(prevRecords) ? prevRecords : [];
					rightRecords.push(r.tuple.getValue());

					const zOutput = {
						tuple: makeOutput(
							l.tuple,
							Tuple.from<R[]>(
								`aggregate(${l.tuple.id})`,
								l.tuple.getOwner(),
								rightRecords
							)
						),
						weight: l.weight * 1,
					};
					out = out.add(zOutput);
					newState.aggregateByK = newState.aggregateByK.set(keyId, zOutput);

					continue;
				}

				out = out.add({
					tuple: makeOutput(l.tuple, r.tuple),
					weight: l.weight * r.weight,
				});
			}
		}

		/* --- 4. integrate state --- */
		newState.leftByK = zsetAddToMap(state.leftByK, leftΔ, kL);
		newState.rightByK = zsetAddToMap(state.rightByK, rightΔ, kR);

		return [out, newState];
	};
}

function zsetAddToMap<Row, K>(
	map: Map<string, ZSet<Row>>,
	delta: ZSet<Row>,
	kFn: (t: Tuple<Row>) => Tuple<K>
) {
	for (const ztuple of delta) {
		const kId = kFn(ztuple.tuple).contentId();
		const bag = map.get(kId, ZSet.empty<Row>()).add(ztuple);

		if (bag.size === 0) {
			map = map.remove(kId);
		} else {
			map = map.set(kId, bag);
		}
	}

	return map;
}

//
// Enriched LeftOuterJoin State.
//

export type LeftOuterJoinState<L, R, Out> = {
	type: "leftOuterJoin";
	leftByK: Arrangement<L>;
	rightByK: Arrangement<R>;
	aggregateByK: Map<string, ZTuple<Out>>;
};

export const emptyLeftOuterJoinState = <L, R, Out>(): LeftOuterJoinState<L, R, Out> => ({
	type: "leftOuterJoin",
	leftByK: Map(),
	rightByK: Map(),
	aggregateByK: Map(),
});

export const hydrateLeftOuterJoinState = <L, R, Out>(
	state: RawLeftOuterJoinState
): LeftOuterJoinState<L, R, Out> => ({
	type: "leftOuterJoin",
	leftByK: hydrateArrangement(state.leftByK),
	rightByK: hydrateArrangement(state.rightByK),
	aggregateByK: Map(
		Object.entries(state.aggregateByK).map(([key, value]) => [key, hydrateZTuple(value)])
	),
});

export const dehydrateLeftOuterJoinState = <L, R, Out>(
	state: LeftOuterJoinState<L, R, Out>
): RawLeftOuterJoinState => ({
	type: "leftOuterJoin",
	leftByK: dehydrateArrangement(state.leftByK),
	rightByK: dehydrateArrangement(state.rightByK),
	aggregateByK: Object.fromEntries(
		state.aggregateByK
			.entrySeq()
			.toArray()
			.map(([key, value]) => [key, dehydrateZTuple(value)] as const)
	),
});

//
// Raw LeftOuterJoin State.
//

export const leftOuterJoinOutputSchema = z.discriminatedUnion("type", [
	z.object({ type: z.literal("tuple") }),
	z.object({ type: z.literal("insertAtPath"), path: jsonpathRefSchema }),
	z.object({
		type: z.literal("insertAggregateAtPath"),
		path: jsonpathRefSchema,
		aggregation: z.enum(["to_array"]),
	}),
]);
export type LeftOuterJoinOutput = z.infer<typeof leftOuterJoinOutputSchema>;

export const leftOuterJoinSpecSchema = z.object({
	type: z.literal("leftOuterJoin"),
	leftKey: jsonpathRefSchema,
	rightKey: jsonpathRefSchema,
	right: subqueryRefSchema,
	output: leftOuterJoinOutputSchema,
});
export type LeftOuterJoinSpec = z.infer<typeof leftOuterJoinSpecSchema>;

export function leftOuterJoinSpec(
	leftKey: JsonPathRef,
	rightKey: JsonPathRef,
	right: SubqueryRef,
	output: LeftOuterJoinOutput
): LeftOuterJoinSpec {
	return {
		type: "leftOuterJoin",
		leftKey,
		rightKey,
		right,
		output,
	};
}

export const rawLeftOuterJoinStateSchema = z.object({
	type: z.literal("leftOuterJoin"),
	leftByK: rawArrangementSchema,
	rightByK: rawArrangementSchema,
	aggregateByK: z.record(z.string(), rawZTupleSchema),
});
export type RawLeftOuterJoinState = z.infer<typeof rawLeftOuterJoinStateSchema>;
